/* eslint-disable react/react-in-jsx-scope */
import React, { useState, useEffect } from "react";
import { getTurnstileErrorByErrorCode } from '../../common/Global';
import Turnstile from 'react-turnstile';
import { ConfigurationVariable, TurnstileErrors, TurnstileStatus } from "../../common/CommonEnums";
import { useDispatch } from "react-redux";
import { resetTurnstileRetryCount } from "../../actions/LoginAction";
import useTextCaptcha from "./useTextCaptcha";

/*
 * useTurnstileCaptcha - This hook is used for handling/loading turnstile captcha 
 *                       along with text captcha(if required).
 * It uses Turnstile Widget which will generate a new token or any error if turnstile
 * unable to pass challenge in the browser.
 * If somehow turnstile fails then it will load text captcha with help of useTextCaptcha
 * hook.
 * We can reset turnstile token with help of its callback.
 * We can turn on/off turnstile with help of common variable.3M
 */
const useTurnstileCaptcha = (pageNumber) => {
    const isAutomationEnv = process.env.REACT_APP_CONFIGURATION_VARIABLE === ConfigurationVariable.Automation;
    const turnstileTimeout = Number(process.env.REACT_APP_TURNSTILE_TIMEOUT);
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
    const turnstileCaptcha = isAutomationEnv ? <></> : <Turnstile sitekey={siteKey}
                                                                  onVerify={(token) => verifyToken(token)}
                                                                  action={'RAMANSUJATI_SAMAJ_LOGIN'}
                                                                  className='turnstile-captcha'
                                                                  retry='never'
                                                                  onError={(error) => errorInTurnstile(error)}
                                                                  onUnsupported={() => turnstileNotSupportedOnBrowser()} />;
    const [turnstileToken, setTurnstileToken] = useState(isAutomationEnv ? 'Automation Testing' : '');
    const [turnstileError, setTurnstileError] = useState(null);
    const [isTurnstileLoading, setIsTurnstileLoading] = useState(isAutomationEnv ? false : true);
    const [showTextBasedCaptcha, setShowTextBasedCaptcha] = useState(false);
    const [timeoutTriggered, setTimeoutTriggered] = useState(false);
    const dispatch = useDispatch();
    const { textCaptcha, onRefreshCaptcha, isCaptchaInvalid, captcha, captchaImage } = useTextCaptcha(pageNumber);
    const IS_TURNSTILE_UP = TurnstileStatus.UP;

    // Auto-fallback to text captcha if turnstile takes more than 5 seconds
    useEffect(() => {
        let timeoutId;
        if (isTurnstileLoading && !turnstileToken && !isAutomationEnv && IS_TURNSTILE_UP && !timeoutTriggered) {
            timeoutId = setTimeout(() => {
                setTimeoutTriggered(true);
                setShowTextBasedCaptcha(true);
                setTurnstileError('Turnstile is taking longer than expected. Please use the alternative captcha below.');
                setIsTurnstileLoading(false);
            }, turnstileTimeout); // 15 seconds timeout
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isTurnstileLoading, turnstileToken, isAutomationEnv, IS_TURNSTILE_UP, timeoutTriggered]);

    const verifyToken = (token) => {
        // Only update state if timeout hasn't been triggered (prevent race condition)
        if (!timeoutTriggered) {
            dispatch(resetTurnstileRetryCount());
            setTurnstileToken(token);
            setIsTurnstileLoading(false);
        }
    }

    const errorInTurnstile = (errorCode) => {
        errorCode = errorCode || '';
        const { errorMessageToBeShown } = getTurnstileErrorByErrorCode(errorCode.toString());

        setTurnstileToken('');
        setTurnstileError(errorMessageToBeShown);
        setIsTurnstileLoading(false);
        setShowTextBasedCaptcha(true);
        setTimeoutTriggered(true); // Mark as handled to prevent race conditions
    }

    const turnstileNotSupportedOnBrowser = () => {
        setTurnstileToken(TurnstileErrors.TurnstileErrorForUnsupportedBrowser);
        setTurnstileError('');
        setIsTurnstileLoading(false);
    }

    const resetTurnstileToken = () => {
        if (!isAutomationEnv) {
            setTurnstileToken('');
            setIsTurnstileLoading(true);
            setShowTextBasedCaptcha(false);
            setTurnstileError(null);
            setTimeoutTriggered(false); // Reset timeout flag for fresh attempt
            if (window && window.turnstile)
                window.turnstile.reset();
        }
    }

    const getTurnstileProps = () => {
        let turnstileProps = {
            turnstileCaptcha,
            turnstileToken,
            turnstileError,
            resetTurnstileToken,
            isTurnstileUp: IS_TURNSTILE_UP,
            isTurnstileLoading
        };

        return turnstileProps;
    }

    if (IS_TURNSTILE_UP) {
        if (showTextBasedCaptcha) {
            return {
                ...getTurnstileProps(),
                textCaptcha, onRefreshCaptcha, isCaptchaInvalid, captcha, captchaImage
            };
        }
        else
            return {
                ...getTurnstileProps(),
                textCaptcha: <></>, onRefreshCaptcha, isCaptchaInvalid: false, captcha: '', captchaImage: ''
            };
    } else {
        return {
            turnstileCaptcha: <></>,
            turnstileToken: '',
            turnstileError: '',
            resetTurnstileToken,
            isTurnstileUp: IS_TURNSTILE_UP,
            isTurnstileLoading: false,
            textCaptcha, onRefreshCaptcha, isCaptchaInvalid, captcha, captchaImage
        }
    }

};

export default useTurnstileCaptcha;